/**
 * Generic Color Utilities
 *
 * Provides type-safe color conversion and manipulation utilities.
 * Works with any CSS color format (hex, rgb, rgba, oklch, hsl, etc.)
 *
 * Core use cases:
 * - Convert between color formats (OKLCH ↔ hex ↔ RGB)
 * - Determine optimal text color for backgrounds (contrast)
 * - Generate CSS color strings for styles
 *
 * Based on the Culori color library for accurate color space conversions.
 */

import { formatHex, parse, rgb, wcagContrast } from 'culori'
import colors from 'tailwindcss/colors'

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Tailwind CSS color names (excluding deprecated colors)
 */
export type TailwindColorName =
  | 'red' | 'orange' | 'amber' | 'yellow' | 'lime' | 'green' | 'emerald'
  | 'teal' | 'cyan' | 'sky' | 'blue' | 'indigo' | 'violet' | 'purple'
  | 'fuchsia' | 'pink' | 'rose' | 'slate' | 'gray' | 'zinc' | 'neutral' | 'stone'

/**
 * Tailwind CSS color shades
 */
export type TailwindShade = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950

/**
 * Nuxt UI semantic color names
 */
export type SemanticColor = 'success' | 'error' | 'warning' | 'info' | 'secondary'

/**
 * Accepts any valid CSS color string or Tailwind color notation
 * Examples:
 * - CSS: "#22c55e", "rgb(34, 197, 94)", "oklch(72.3% 0.219 149.579)"
 * - Hex without #: "22c55e", "fff"
 * - Tailwind: "pink", "pink-400", "emerald-600"
 * - Semantic: "success", "error", "warning", "info"
 */
export type ColorInput = string

/**
 * Text color for optimal contrast: neutral-900 (soft dark) or white
 * Returns Tailwind color notation for theme-independent text colors
 */
export type ContrastText = 'neutral-900' | 'white'

// ============================================================================
// SEMANTIC COLOR MAPPING
// ============================================================================

/**
 * Nuxt UI semantic colors mapped to Tailwind colors
 * Based on Nuxt UI v4 default color system
 */
const SEMANTIC_COLOR_MAP: Record<SemanticColor, TailwindColorName> = {
  success: 'green',
  error: 'red',
  warning: 'yellow',
  info: 'blue',
  secondary: 'blue',
}

/**
 * Default shade to use when no shade is specified
 */
const DEFAULT_SHADE = 500

// ============================================================================
// CORE CONVERSION FUNCTIONS
// ============================================================================

/**
 * Resolve Tailwind color notation to actual color value
 *
 * Supports multiple input formats:
 * - CSS colors: "#22c55e", "rgb(34, 197, 94)", "oklch(...)" → returns as-is
 * - Hex without #: "22c55e", "fff" → adds # prefix and returns
 * - Semantic colors: "success", "error", "warning" → maps to Tailwind colors
 * - Tailwind with shade: "pink-400", "emerald-600" → resolves to OKLCH value
 * - Tailwind without shade: "pink", "green" → uses default shade (500)
 *
 * @param color - Color input (any CSS color, Tailwind notation, or semantic)
 * @returns Resolved color string (OKLCH format for Tailwind colors) or null if invalid
 *
 * @example
 * resolveColor("#22c55e") // → "#22c55e" (CSS color, unchanged)
 * resolveColor("22c55e") // → "#22c55e" (hex without #)
 * resolveColor("success") // → "oklch(...)" (semantic → green-500)
 * resolveColor("pink-400") // → "oklch(...)" (Tailwind with shade)
 * resolveColor("pink") // → "oklch(...)" (Tailwind → pink-500)
 * resolveColor("invalid") // → null
 */
export function resolveColor(color: ColorInput): string | null {
  // 1. If already a valid CSS color (hex, rgb, oklch, etc.), return as-is
  let parsed = parse(color)
  if (parsed) {
    return color
  }

  // 1.5. Try adding # prefix if it looks like hex without # (e.g., "ec4899" or "fff")
  if (/^[0-9a-fA-F]{6}$/.test(color) || /^[0-9a-fA-F]{3}$/.test(color)) {
    const colorWithHash = '#' + color
    parsed = parse(colorWithHash)
    if (parsed) {
      return colorWithHash
    }
  }

  // 2. Check if it's a semantic color (success, error, etc.)
  const semanticColor = color.toLowerCase() as SemanticColor
  if (semanticColor in SEMANTIC_COLOR_MAP) {
    const tailwindColor = SEMANTIC_COLOR_MAP[semanticColor]
    // Use default shade (500) for semantic colors
    return getTailwindColor(tailwindColor, DEFAULT_SHADE)
  }

  // 3. Check if it's Tailwind notation with shade (e.g., "pink-400")
  const shadeMatch = color.match(/^([a-z]+)-(\d+)$/i)
  if (shadeMatch && shadeMatch[1] && shadeMatch[2]) {
    const colorName = shadeMatch[1]
    const shade = parseInt(shadeMatch[2], 10) as TailwindShade
    return getTailwindColor(colorName, shade)
  }

  // 4. Check if it's a Tailwind color without shade (e.g., "pink")
  const colorName = color.toLowerCase()
  if (colorName in colors && typeof colors[colorName as keyof typeof colors] === 'object') {
    return getTailwindColor(colorName, DEFAULT_SHADE)
  }

  // 5. Invalid color
  return null
}

/**
 * Get Tailwind color value from color name and shade
 *
 * @param colorName - Tailwind color name (e.g., "pink", "green")
 * @param shade - Color shade (50-950)
 * @returns OKLCH color string or null if invalid
 *
 * @internal
 */
function getTailwindColor(colorName: string, shade: TailwindShade): string | null {
  const colorPalette = colors[colorName as keyof typeof colors]

  // Check if color exists and is an object (not a string like 'inherit')
  if (!colorPalette || typeof colorPalette !== 'object') {
    return null
  }

  // Get the specific shade - convert number to string for palette lookup
  const shadeKey = shade.toString() as '50' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900' | '950'
  const colorValue = colorPalette[shadeKey]

  // Tailwind v4 colors are OKLCH format
  return typeof colorValue === 'string' ? colorValue : null
}

/**
 * Convert any color to hex format
 *
 * @param color - Color input (any CSS color format, Tailwind notation, or semantic)
 * @returns Hex color string with # prefix
 *
 * @example
 * toHex("oklch(72.3% 0.219 149.579)") // → "#22c55e"
 * toHex("rgb(34, 197, 94)") // → "#22c55e"
 * toHex("pink") // → "#ec4899" (pink-500)
 * toHex("pink-400") // → "#f9a8d4"
 * toHex("success") // → "#22c55e" (green-500)
 */
export function toHex(color: ColorInput): string {
  // Resolve Tailwind/semantic notation first
  const resolved = resolveColor(color)
  if (!resolved) return '#000000'

  // Parse and format (resolved is valid, so parse will succeed)
  return formatHex(parse(resolved)!)
}

/**
 * Convert any color to CSS rgb() function
 *
 * @param color - Color input (any CSS color format, Tailwind notation, or semantic)
 * @returns CSS rgb() function string
 *
 * @example
 * toRgb("#22c55e") // → "rgb(34, 197, 94)"
 * toRgb("oklch(72.3% 0.219 149.579)") // → "rgb(34, 197, 94)"
 * toRgb("pink") // → "rgb(236, 72, 153)" (pink-500)
 * toRgb("success") // → "rgb(34, 197, 94)" (green-500)
 */
export function toRgb(color: ColorInput): string {
  // Resolve Tailwind/semantic notation first
  const resolved = resolveColor(color)
  if (!resolved) return 'rgb(0, 0, 0)'

  // Parse and convert to RGB (resolved is valid, so parse will succeed)
  const rgbColor = rgb(parse(resolved)!)!
  const r = Math.round((rgbColor.r ?? 0) * 255)
  const g = Math.round((rgbColor.g ?? 0) * 255)
  const b = Math.round((rgbColor.b ?? 0) * 255)

  return `rgb(${r}, ${g}, ${b})`
}

/**
 * Convert any color to CSS rgba() function with custom alpha
 *
 * @param color - Color input (any CSS color format, Tailwind notation, or semantic)
 * @param alpha - Alpha transparency value (0-1)
 * @returns CSS rgba() function string
 *
 * @example
 * toRgba("#22c55e", 0.5) // → "rgba(34, 197, 94, 0.5)"
 * toRgba("oklch(72.3% 0.219 149.579)", 0.2) // → "rgba(34, 197, 94, 0.2)"
 * toRgba("pink", 0.3) // → "rgba(236, 72, 153, 0.3)" (pink-500)
 */
export function toRgba(color: ColorInput, alpha: number): string {
  // Resolve Tailwind/semantic notation first
  const resolved = resolveColor(color)
  if (!resolved) return `rgba(0, 0, 0, ${alpha})`

  // Parse and convert to RGB (resolved is valid, so parse will succeed)
  const rgbColor = rgb(parse(resolved)!)!
  const r = Math.round((rgbColor.r ?? 0) * 255)
  const g = Math.round((rgbColor.g ?? 0) * 255)
  const b = Math.round((rgbColor.b ?? 0) * 255)

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

// ============================================================================
// CONTRAST & ACCESSIBILITY
// ============================================================================

/**
 * Determine optimal text color for a background color using WCAG contrast ratios
 * Returns Tailwind color notation: "neutral-900" (soft dark) or "white"
 *
 * @param backgroundColor - Background color (any CSS color format, Tailwind notation, or semantic)
 * @returns "neutral-900" for light backgrounds, "white" for dark backgrounds
 *
 * @example
 * getContrastText("pink-500") // → "white" (dark background)
 * getContrastText("amber-500") // → "neutral-900" (light background)
 * getContrastText("success") // → "white" (green-500 is dark)
 * getContrastText("#22c55e") // → "white"
 *
 * @remarks
 * Uses WCAG 2.0 contrast ratio algorithm to determine which text color
 * provides better contrast against the background. Compares contrast ratios
 * for both neutral-900 and white, returns the one with higher contrast.
 */
export function getContrastText(backgroundColor: ColorInput): ContrastText {
  // Resolve and parse background color
  const resolved = resolveColor(backgroundColor)
  if (!resolved) return 'white' // Only fallback needed - for invalid colors

  // Parse colors (these will always succeed if resolved is valid)
  const bgColor = parse(resolved)!
  const darkColor = parse(getTailwindColor('neutral', 900)!)!

  // Calculate WCAG contrast ratios
  const whiteContrast = wcagContrast(bgColor, 'white')!
  const darkContrast = wcagContrast(bgColor, darkColor)!

  // Smart selection logic:
  // If white has good enough contrast (≥3.5:1) AND dark is not significantly better (≤1.5x),
  // prefer white for a more vibrant appearance while maintaining accessibility
  const whiteIsAccessible = whiteContrast >= 3.5
  const ratio = darkContrast / whiteContrast
  const darkNotMuchBetter = ratio <= 1.5

  if (whiteIsAccessible && darkNotMuchBetter) {
    return 'white'
  }

  // Otherwise, pick the higher contrast
  return whiteContrast > darkContrast ? 'white' : 'neutral-900'
}

/**
 * Get optimal text color as hex format
 * Converts Tailwind notation (neutral-900/white) to hex values
 *
 * @param backgroundColor - Background color (any CSS color format)
 * @returns "#171717" (neutral-900) or "#ffffff" (white)
 *
 * @example
 * getContrastTextHex("#22c55e") // → "#ffffff"
 * getContrastTextHex("amber-500") // → "#171717" (neutral-900)
 */
export function getContrastTextHex(backgroundColor: ColorInput): string {
  const textColor = getContrastText(backgroundColor)
  return textColor === 'white' ? '#ffffff' : toHex('neutral-900')
}

